/**
 * Cookie-based storage for feature flag values
 * @module lib/feature-flags/store
 */

import Cookie from "js-cookie";
import { FEATURE_FLAG_INTERNAL_COOKIE_NAME } from "./constants";

const ONE_YEAR = 365;

const cookieAttributes = {
  expires: ONE_YEAR,
  secure: process.env.NODE_ENV !== "development",
  sameSite: "Lax",
  path: "/",
};

/**
 * Creates a flag store backed by a cookie
 * @param {string} cookieName - Name of the cookie to use for storage
 * @returns {Object} Store with get, set, and clear methods
 */
const FlagStore = (cookieName) => ({
  /**
   * Gets all flag values from the cookie
   * @returns {Object} Object mapping flag keys to variant values
   */
  get: () => {
    return deserializeFlagValues(Cookie.get(cookieName));
  },

  /**
   * Sets flag values in the cookie
   * @param {Object} flagValues - Object mapping flag keys to variant values
   */
  set: (flagValues) => {
    Cookie.set(cookieName, serializeFlagValues(flagValues), cookieAttributes);
  },

  /**
   * Clears all flag values from the cookie
   */
  clear: () => {
    Cookie.remove(cookieName, cookieAttributes);
  },
});

/** Store for internal override flags (e.g., from /ab route) */
export const InternalFlags = FlagStore(FEATURE_FLAG_INTERNAL_COOKIE_NAME);

/**
 * Converts raw cookie value into an object with key-value pairs
 * @param {string} rawFlagValues - Comma-separated key=value string
 * @returns {Object} Object mapping flag keys to variant values
 * @example
 * // input: "flag-a=myvariant,flag-b=anothervariant"
 * // output: { 'flag-a': 'myvariant', 'flag-b': 'anothervariant' }
 */
const deserializeFlagValues = (rawFlagValues) => {
  const flagValues = (
    rawFlagValues != null && rawFlagValues.length > 0
      ? rawFlagValues.split(",").map((c) => c.split("="))
      : []
  ).reduce((acc, val) => {
    const [k, v] = val;
    acc[k] = v;
    return acc;
  }, {});
  return flagValues;
};

/**
 * Converts flag values object into serialized form
 * @param {Object} flagValues - Object mapping flag keys to variant values
 * @returns {string} Comma-separated key=value string, sorted alphabetically
 * @example
 * // input: { 'flag-a': 'myvariant', 'flag-b': 'anothervariant' }
 * // output: "flag-a=myvariant,flag-b=anothervariant"
 */
const serializeFlagValues = (flagValues) => {
  const flagKeys = Object.keys(flagValues);
  flagKeys.sort();
  const serializedFlagValues = flagKeys
    .map((k) => `${k}=${flagValues[k]}`)
    .join(",");
  return serializedFlagValues;
};
